import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UserPlus, Trash2, Shield, User, Link as LinkIcon, Unlink, Users, Mail } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";


// Nur diese 4 Rollen sind erlaubt - gemäß der Rollenmatrix
type AppRole = 'admin' | 'hr_manager' | 'hr_clerk' | 'employee';

// Rollenlabels für die UI
const ROLE_LABELS: Record<AppRole, string> = {
  admin: "Administrator",
  hr_manager: "Personalmanager",
  hr_clerk: "Personalverwalter",
  employee: "Mitarbeiter",
};

// Rollen in hierarchischer Reihenfolge
const AVAILABLE_ROLES: AppRole[] = ['admin', 'hr_manager', 'hr_clerk', 'employee'];

interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  created_at: string;
}


interface Employee {
  id: string;
  name: string;
  position: string;
  department: string | null;
  business_email: string | null;
  private_email: string | null;
}

interface Profile {
  id: string;
  full_name: string;
  email: string;
  employee_id: string | null;
}

const UserManagement = () => {
  const { userRole: currentUserRole } = useAuth();
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [allProfiles, setAllProfiles] = useState<Profile[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string | null>(null);
  
  // Dialog states - NEU: Mehrfachrollen
  const [assignRoleDialogOpen, setAssignRoleDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<string>("");
  const [selectedRoles, setSelectedRoles] = useState<AppRole[]>(['employee']);
  
  // Link dialog
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedUserForLink, setSelectedUserForLink] = useState<UserRole | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  
  // Search
  const [employeeSearch, setEmployeeSearch] = useState("");
  
  // Only admin can assign/modify roles
  const canManageRoles = currentUserRole === 'admin';

  useEffect(() => {
    fetchUserRoles();
    fetchAllProfiles();
    fetchEmployees();
  }, []);

  const fetchUserRoles = async () => {
    try {
      // Keine direkte profiles-Join-Abfrage hier, da die profiles-Tabelle per RLS geschützt ist.
      // Profilinfos kommen über get_all_profiles_for_admin().
      const { data, error } = await supabase
        .from('user_roles')
        .select('id, user_id, role, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filter nur gültige Rollen (keine vorarbeiter oder andere alte Rollen)
      const validRoles = (data || []).filter(ur => AVAILABLE_ROLES.includes(ur.role as AppRole));
      setUserRoles(validRoles as UserRole[]);
    } catch (error) {
      console.error('Error fetching user roles:', error);
      toast.error("Fehler beim Laden der Benutzerrollen");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProfiles = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_all_profiles_for_admin');

      if (error) throw error;
      setAllProfiles(data || []);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('id, name, position, department, business_email, private_email')
        .is('archived_at', null)
        .order('name');

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const getLinkedProfile = (employeeId: string) => {
    return allProfiles.find(p => p.employee_id === employeeId);
  };

  const getProfileById = (profileId: string | null | undefined) => {
    if (!profileId) return undefined;
    return allProfiles.find(p => p.id === profileId);
  };

  // NEU: Gibt ALLE Rollen eines Mitarbeiters zurück
  const getEmployeeUserRoles = (employeeId: string): UserRole[] => {
    const profile = getLinkedProfile(employeeId);
    if (!profile) return [];
    return userRoles.filter(ur => ur.user_id === profile.id);
  };

  // Für Anzeige: Gibt die primäre (höchste) Rolle zurück
  const getEmployeePrimaryRole = (employeeId: string): UserRole | null => {
    const roles = getEmployeeUserRoles(employeeId);
    if (roles.length === 0) return null;
    
    // Sortiere nach Priorität
    const priority: Record<AppRole, number> = { admin: 1, hr_manager: 2, hr_clerk: 3, employee: 4 };
    return roles.sort((a, b) => (priority[a.role] || 99) - (priority[b.role] || 99))[0];
  };

  const openAssignRoleDialog = (employee: Employee) => {
    setSelectedEmployee(employee);
    const linkedProfile = getLinkedProfile(employee.id);
    setSelectedProfileId(linkedProfile?.id || "");
    // NEU: Lade alle bestehenden Rollen
    const existingRoles = getEmployeeUserRoles(employee.id).map(ur => ur.role);
    setSelectedRoles(existingRoles.length > 0 ? existingRoles : ['employee']);
    setAssignRoleDialogOpen(true);
  };

  // NEU: Mehrfachrollen-Zuweisung
  const handleAssignRoles = async () => {
    if (!selectedEmployee || !selectedProfileId) {
      toast.error("Bitte wählen Sie einen Benutzer aus");
      return;
    }

    if (selectedRoles.length === 0) {
      toast.error("Bitte wählen Sie mindestens eine Rolle aus");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Mitarbeiter mit Profil verknüpfen falls noch nicht geschehen
      const linkedProfile = getLinkedProfile(selectedEmployee.id);
      if (!linkedProfile || linkedProfile.id !== selectedProfileId) {
        const { error: linkError } = await supabase
          .from('profiles')
          .update({ employee_id: selectedEmployee.id })
          .eq('id', selectedProfileId);
        
        if (linkError) throw linkError;
      }

      // ALLE bestehenden Rollen des Benutzers löschen
      const { error: deleteError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', selectedProfileId);
      
      if (deleteError) throw deleteError;

      // ALLE ausgewählten Rollen einfügen
      const rolesToInsert = selectedRoles.map(role => ({
        user_id: selectedProfileId,
        role: role,
        created_by: user?.id
      }));

      const { error: insertError } = await supabase
        .from('user_roles')
        .insert(rolesToInsert);

      if (insertError) throw insertError;

      const roleNames = selectedRoles.map(r => ROLE_LABELS[r]).join(", ");
      toast.success(`Rollen erfolgreich zugewiesen: ${roleNames}`);

      setAssignRoleDialogOpen(false);
      setSelectedEmployee(null);
      setSelectedProfileId("");
      setSelectedRoles(['employee']);
      
      // Beide Daten neu laden und auf Abschluss warten
      await Promise.all([fetchUserRoles(), fetchAllProfiles()]);
    } catch (error) {
      console.error('Error assigning roles:', error);
      toast.error("Fehler beim Zuweisen der Rollen");
    }
  };

  // Toggle-Funktion für Rollen-Checkboxen
  const toggleRole = (role: AppRole) => {
    setSelectedRoles(prev => {
      if (prev.includes(role)) {
        // Mindestens eine Rolle muss bleiben
        if (prev.length === 1) {
          toast.error("Mindestens eine Rolle muss zugewiesen sein");
          return prev;
        }
        return prev.filter(r => r !== role);
      } else {
        return [...prev, role];
      }
    });
  };

  const handleDeleteRole = (roleId: string) => {
    setRoleToDelete(roleId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!roleToDelete) return;

    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('id', roleToDelete);

      if (error) throw error;

      toast.success("Benutzerrolle erfolgreich entfernt");
      fetchUserRoles();
    } catch (error) {
      console.error('Error deleting user role:', error);
      toast.error("Fehler beim Entfernen der Benutzerrolle");
    } finally {
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    }
  };

  const openLinkDialog = (userRole: UserRole) => {
    setSelectedUserForLink(userRole);
    const profile = getProfileById(userRole.user_id);
    setSelectedEmployeeId(profile?.employee_id || "none");
    setLinkDialogOpen(true);
  };

  const handleLinkEmployee = async () => {
    if (!selectedUserForLink) return;

    try {
      const employeeIdToSave = selectedEmployeeId === "none" ? null : selectedEmployeeId;
      const { error } = await supabase
        .from('profiles')
        .update({ employee_id: employeeIdToSave })
        .eq('id', selectedUserForLink.user_id);

      if (error) throw error;

      toast.success(employeeIdToSave 
        ? "Benutzer erfolgreich mit Mitarbeiter verknüpft" 
        : "Verknüpfung erfolgreich aufgehoben"
      );
      setLinkDialogOpen(false);
      setSelectedUserForLink(null);
      setSelectedEmployeeId("none");
      fetchUserRoles();
      fetchAllProfiles();
    } catch (error) {
      console.error('Error linking employee:', error);
      toast.error("Fehler beim Verknüpfen");
    }
  };

  const getLinkedEmployeeName = (employeeId: string | null) => {
    if (!employeeId) return null;
    const employee = employees.find(e => e.id === employeeId);
    return employee ? employee.name : null;
  };

  const getAvailableEmployees = () => {
    const linkedEmployeeIds = allProfiles
      .filter(p => p.employee_id && p.id !== selectedUserForLink?.user_id)
      .map(p => p.employee_id);
    
    return employees.filter(e => !linkedEmployeeIds.includes(e.id));
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-destructive text-destructive-foreground';
      case 'hr_manager':
        return 'bg-blue-500 text-white';
      case 'hr_clerk':
        return 'bg-emerald-500 text-white';
      case 'employee':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-secondary text-secondary-foreground';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
      case 'hr_manager':
      case 'hr_clerk':
        return <Shield className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const filteredEmployees = employees.filter(emp => 
    emp.name.toLowerCase().includes(employeeSearch.toLowerCase()) ||
    emp.position.toLowerCase().includes(employeeSearch.toLowerCase()) ||
    (emp.department && emp.department.toLowerCase().includes(employeeSearch.toLowerCase()))
  );

  const getAvailableProfilesForEmployee = (employee: Employee) => {
    const availableProfiles = allProfiles.filter(p => 
      !p.employee_id || p.employee_id === employee.id
    );
    
    return availableProfiles;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">Laden...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Benutzerverwaltung</CardTitle>
          <CardDescription>
            Verwalten Sie Benutzerrollen und verknüpfen Sie Mitarbeiter mit Systemkonten.
            {!canManageRoles && <span className="text-destructive ml-2">(Nur Admin kann Rollen ändern)</span>}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

            {/* Mitarbeiter-Rollenzuweisung */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Input
                  placeholder="Mitarbeiter suchen..."
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  className="max-w-sm"
                />
              </div>

              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mitarbeiter</TableHead>
                      <TableHead>Position</TableHead>
                      <TableHead>Abteilung</TableHead>
                      <TableHead>Verknüpfter Benutzer</TableHead>
                      <TableHead>Rolle</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEmployees.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Keine Mitarbeiter gefunden
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredEmployees.map((employee) => {
                        const linkedProfile = getLinkedProfile(employee.id);
                        const employeeRoles = getEmployeeUserRoles(employee.id);
                        const primaryRole = getEmployeePrimaryRole(employee.id);
                        
                        return (
                          <TableRow key={employee.id}>
                            <TableCell className="font-medium">{employee.name}</TableCell>
                            <TableCell>{employee.position}</TableCell>
                            <TableCell>{employee.department || '-'}</TableCell>
                            <TableCell>
                              {linkedProfile ? (
                                <div className="flex items-center gap-2">
                                  <Mail className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm">{linkedProfile.email}</span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">Nicht verknüpft</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {employeeRoles.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {employeeRoles.map(ur => (
                                    <Badge key={ur.id} className={getRoleBadgeColor(ur.role)}>
                                      {ROLE_LABELS[ur.role as AppRole] || ur.role}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">Keine Rolle</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {canManageRoles && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openAssignRoleDialog(employee)}
                                  className="gap-1"
                                >
                                  <UserPlus className="h-4 w-4" />
                                  {employeeRoles.length > 0 ? "Rollen ändern" : "Rollen zuweisen"}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Systembenutzer ohne Mitarbeiterverknüpfung */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Systembenutzer</h3>
                <div className="space-y-3">
                  {userRoles.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Keine Benutzerrollen vorhanden.
                    </div>
                  ) : (
                    userRoles.map((userRole) => {
                      const profile = getProfileById(userRole.user_id);
                      const linkedEmployeeName = getLinkedEmployeeName(profile?.employee_id || null);

                      return (
                        <Card key={userRole.id}>
                          <CardContent className="pt-6">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                              <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                  {getRoleIcon(userRole.role)}
                                </div>
                                <div>
                                  <h3 className="font-semibold">
                                    {profile?.full_name || profile?.email || userRole.user_id}
                                  </h3>
                                  <p className="text-sm text-muted-foreground">
                                    {profile?.email || "-"}
                                  </p>
                                  {linkedEmployeeName && (
                                    <p className="text-sm text-primary flex items-center gap-1 mt-1">
                                      <LinkIcon className="h-3 w-3" />
                                      Verknüpft mit: {linkedEmployeeName}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 flex-wrap">
                                <Badge className={getRoleBadgeColor(userRole.role)}>
                                  {ROLE_LABELS[userRole.role as AppRole] || userRole.role}
                                </Badge>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openLinkDialog(userRole)}
                                  className="gap-1"
                                >
                                  {profile?.employee_id ? (
                                    <>
                                      <Unlink className="h-3 w-3" />
                                      Verknüpfung ändern
                                    </>
                                  ) : (
                                    <>
                                      <LinkIcon className="h-3 w-3" />
                                      Mit Mitarbeiter verknüpfen
                                    </>
                                  )}
                                </Button>
                                {canManageRoles && (
                                  <Button
                                    variant="destructive"
                                    size="icon"
                                    onClick={() => handleDeleteRole(userRole.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
        </CardContent>
      </Card>

      {/* Delete Role Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzerrolle entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie diese Benutzerrolle entfernen möchten? 
              Der Benutzer verliert damit seine Zugriffsrechte.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Entfernen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Link Employee Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Benutzer mit Mitarbeiter verknüpfen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-muted-foreground">
              Verknüpfen Sie <strong>{getProfileById(selectedUserForLink?.user_id)?.full_name || getProfileById(selectedUserForLink?.user_id)?.email || selectedUserForLink?.user_id}</strong> mit einem Mitarbeiter,
              damit der Benutzer sein eigenes Profil einsehen kann.
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium">Mitarbeiter auswählen</label>
              <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                <SelectTrigger>
                  <SelectValue placeholder="Mitarbeiter wählen..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Verknüpfung</SelectItem>
                  {getAvailableEmployees().map(employee => (
                    <SelectItem key={employee.id} value={employee.id}>
                      {employee.name} - {employee.position}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleLinkEmployee}>
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Role to Employee Dialog */}
      <Dialog open={assignRoleDialogOpen} onOpenChange={setAssignRoleDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Rolle zuweisen für {selectedEmployee?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-muted-foreground">
              Wählen Sie einen Systembenutzer und eine Rolle für diesen Mitarbeiter.
              Die Berechtigungen werden automatisch aus der Rollenmatrix übernommen.
            </p>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Benutzer (E-Mail-Konto)</label>
              <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
                <SelectTrigger>
                  <SelectValue placeholder="Benutzer wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {selectedEmployee && getAvailableProfilesForEmployee(selectedEmployee).map(profile => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {profile.email} {profile.full_name ? `(${profile.full_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Der Benutzer muss sich zuerst im System registriert haben.
              </p>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium">Rollen (Mehrfachauswahl möglich)</label>
              <p className="text-xs text-muted-foreground mb-2">
                Wählen Sie eine oder mehrere Rollen. Die Berechtigungen werden kombiniert (Deny-wins).
              </p>
              <div className="space-y-2 border rounded-lg p-3">
                {AVAILABLE_ROLES.map(role => (
                  <div 
                    key={role} 
                    className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer"
                    onClick={() => toggleRole(role)}
                  >
                    <Checkbox
                      checked={selectedRoles.includes(role)}
                      onCheckedChange={() => toggleRole(role)}
                      id={`role-${role}`}
                    />
                    <div className="flex-1">
                      <label htmlFor={`role-${role}`} className="font-medium cursor-pointer">
                        {ROLE_LABELS[role]}
                      </label>
                      <p className="text-xs text-muted-foreground">
                        {role === 'admin' && 'Voller Zugriff auf alle Funktionen'}
                        {role === 'hr_manager' && 'HR-Vollzugriff inkl. Personalkosten'}
                        {role === 'hr_clerk' && 'Mitarbeiterverwaltung ohne Personalkosten'}
                        {role === 'employee' && 'Nur eigene Daten (Self-Service)'}
                      </p>
                    </div>
                    <Badge className={getRoleBadgeColor(role)}>
                      {ROLE_LABELS[role]}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setAssignRoleDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleAssignRoles} disabled={!selectedProfileId || selectedRoles.length === 0}>
                Rollen zuweisen ({selectedRoles.length})
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserManagement;
